/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Plus, Search, Edit, Trash2, Tag, Scale } from "lucide-react"
import { CaseCategoryFormPopup } from "@/components/case-category-form-popup"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import apiService from "@/services/apiService"
import useErrorStore from "@/store/confirmationStore"

interface CaseCategory {
  id: number
  icon?: string
  title: string
  ipcSections: string[]
  bnsSections: string[]
  createdAt: string
  updatedAt: string
  parentId?: number | null
  subcategories?: any[]
}

export default function CaseCategories() {
  const [categories, setCategories] = useState<CaseCategory[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<CaseCategory | null>(null)
  const [deleteCategory, setDeleteCategory] = useState<CaseCategory | null>(null)
  const showError = useErrorStore((s) => s.showError)

  const fetchCategories = async () => {
    try {
      const jsonResponse:any = await apiService.get('case-category?isParent=true');
      setCategories(jsonResponse)
    } catch (error) {
      console.error("Error fetching categories:", error)
      showError("Failed to load categories")
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  const filteredCategories = categories.filter((category) =>
    category.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.bnsSections.some((section) => section.includes(searchTerm)) ||
    category.ipcSections.some((section) => section.includes(searchTerm))
  )

  const handleAddCategory = async (categoryData: Omit<CaseCategory, "id" | "createdAt" | "updatedAt">) => {
    try {
      const newCategory: CaseCategory = await apiService.post('case-category',JSON.stringify(categoryData));
      setCategories((prev) => [...prev, newCategory])
      toast("Category added successfully")
      setIsFormOpen(false)
    } catch (error) {
      console.error("Add category failed:", error)
      showError("Failed to add category")
    }
  }

  const handleEditCategory = async (categoryData: Omit<CaseCategory, "id" | "createdAt" | "updatedAt">) => {
    if (!editingCategory) return
    const updatedCategory: CaseCategory = {
      ...editingCategory,
      ...categoryData,
      updatedAt: new Date().toISOString(),
    }
    try {
      const response = await apiService.put(`case-category/${editingCategory.id}`, JSON.stringify(updatedCategory))
      if (!response) throw new Error("Failed to update category")
      setCategories((prev) =>
        prev.map((cat) => (cat.id === editingCategory.id ? updatedCategory : cat))
      )
      toast("Category updated successfully")
      setEditingCategory(null)
      setIsFormOpen(false)
    } catch (error) {
      console.error("Update category failed:", error)
      showError("Failed to update category")
    }
  }

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return
    try {
      const response = await apiService.delete(`case-category/${deleteCategory.id}`)
      if (!response) throw new Error("Failed to delete category")
      setCategories((prev) => prev.filter((cat) => cat.id !== deleteCategory.id))
      toast("Category deleted successfully")
      setDeleteCategory(null)
    } catch (error) {
      console.error("Delete failed:", error)
      showError("Failed to delete category")
    }
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-black">Case Categories</h1>
          <p className="text-gray-600 mt-1">Manage case categories and their classifications</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
          <Plus className="h-4 w-4 mr-2" /> Add Category
        </Button>
      </div>

      <Card className="border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search categories, aliases, or IPC sections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300"
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map((category:any) => (
          <Card key={category.id} className="border border-gray-200 hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{category.icon || "📋"}</div>
                  <div>
                    <h3 className="font-semibold text-lg text-black">{category.title}</h3>
                    <p className="text-sm text-gray-500">ID: {category.id}</p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingCategory(category)
                      setIsFormOpen(true)
                    }}
                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteCategory(category)}
                    className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {category.subcategories && category.subcategories.slice(0, 3).map((subcategory:any, i:number) => (
                    <Badge key={i} variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                      {subcategory?.title}
                    </Badge>
                  ))}
                  {category.subcategories && category.subcategories.length > 3 && (
                    <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                      +{category.subcategories.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Scale className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">IPC Sections</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {category.ipcSections.slice(0, 4).map((section:any, i:number) => (
                    <Badge key={i} variant="outline" className="text-xs border-gray-300">
                      {section}
                    </Badge>
                  ))}
                  {category.ipcSections.length > 4 && (
                    <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                      +{category.ipcSections.length - 4} more
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Scale className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">BNS Sections</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {category.bnsSections.slice(0, 4).map((section:any, i:number) => (
                    <Badge key={i} variant="outline" className="text-xs border-gray-300">
                      {section}
                    </Badge>
                  ))}
                  {category.bnsSections.length > 4 && (
                    <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                      +{category.bnsSections.length - 4} more
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Scale className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Questioner</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {category.questions.map((question:any) => question.questionText).join(', ')}
                </div>
              </div>

              <div className="text-xs text-gray-500 pt-3 border-t border-gray-100">
                <div>Created: {new Date(category.createdAt).toLocaleDateString("en-IN")}</div>
                <div>Updated: {new Date(category.updatedAt).toLocaleDateString("en-IN")}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {!filteredCategories.length && (
        <Card className="border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Tag className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm ? "Try adjusting your search terms" : "Get started by creating your first case category"}
          </p>
          {!searchTerm && (
            <Button onClick={() => setIsFormOpen(true)} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
              <Plus className="h-4 w-4 mr-2" /> Add Category
            </Button>
          )}
        </Card>
      )}

      <CaseCategoryFormPopup
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setEditingCategory(null)
        }}
        onSubmit={editingCategory ? handleEditCategory : handleAddCategory}
        initialData={editingCategory}
        title={editingCategory ? "Edit Case Category" : "Add New Case Category"}
      />

      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">Delete Category</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Are you sure you want to delete {deleteCategory?.title}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-red-600 text-white hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}